import { createContext, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLingui } from '@lingui/react/macro';

import { StepperNumberItemProps } from '@/design_system/Stepper/StepperNumber/StepperNumberItem/StepperNumberItem';

import { useArticleContext } from './ArticleContext';

interface MultiStepCreationContextData {
  steps: Step[];
  onNextStep: () => void;
  onPreviousStep: () => void;
  goToStepById: (stepId: Step['id']) => void;
  currentStep: Step;
  currentStepIndex: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  getHeaderSteps: () => StepperNumberItemProps[];
}

export type Step = {
  id: 'information' | 'initial-conditions' | 'services';
  name: string;
  status: 'current' | 'pending' | 'completed';
};

const MultiStepCreationContext = createContext({} as MultiStepCreationContextData);

export const MultiStepCreationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useLingui();
  const { view } = useArticleContext();
  const { state } = useLocation();

  const navigate = useNavigate();
  const { hash } = window.location;

  const updateHashWithCurrentStep = (stepId: string) => {
    // The property `state.isNew` is used on the `NewArticle`
    // to know if we are creating a new article or editing.
    // Therefore, when navigating, we need to keep the existing state.
    navigate(`#${stepId}`, { state, replace: true });
  };

  const getCurrentStatusFromHash = (stepId: string) => {
    if (hash === `#${stepId}` || (!hash && stepId === 'information')) {
      return 'current';
    } else if (
      (stepId === 'initial-conditions' && hash === '#services') ||
      (stepId === 'information' && (hash === '#initial-conditions' || hash === '#services'))
    ) {
      return 'completed';
    }

    return 'pending';
  };

  const generateStepsFromView = () => {
    const steps: Step[] = [
      {
        id: 'information',
        name: t({ id: 'requests.new.articles.drawer.information.title', message: 'Information' }),
        status: getCurrentStatusFromHash('information'),
      },
    ];

    if (view?.initialCondition.shown) {
      steps.push({
        id: 'initial-conditions',
        name: t({
          id: 'requests.new.articles.drawer.initial-conditions.title',
          message: 'Initial conditions',
        }),
        status: getCurrentStatusFromHash('initial-conditions'),
      });
    }
    if (view?.services.shown || view?.defects.shown) {
      steps.push({
        id: 'services',
        name: t({ id: 'requests.new.articles.drawer.services.title', message: 'Services' }),
        status: getCurrentStatusFromHash('services'),
      });
    }

    return steps;
  };
  const steps = generateStepsFromView();

  const [currentSteps, setCurrentSteps] = useState<Step[]>(steps);
  const currentStepIndex = currentSteps.findIndex((el) => el.status === 'current');
  const currentStep = currentSteps[currentStepIndex];

  const goToStep = (newIndex: number) => {
    if (newIndex < 0 || newIndex >= currentSteps.length || currentStepIndex === -1) {
      return;
    }
    const steps = [...currentSteps];

    steps[currentStepIndex].status = currentStepIndex > newIndex ? 'pending' : 'completed';
    steps[newIndex].status = 'current';
    setCurrentSteps(steps);
  };

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === currentSteps.length - 1;
  const getHeaderSteps = () => {
    return currentSteps.map((step, index) => ({
      label: step.name,
      status: step.status,
      number: index + 1,
    }));
  };

  const onNextStep = () => {
    if (!isLastStep) {
      goToStep(currentStepIndex + 1);
      updateHashWithCurrentStep(currentSteps[currentStepIndex + 1].id);
    }
  };

  const onPreviousStep = () => {
    if (!isFirstStep) {
      goToStep(currentStepIndex - 1);
      updateHashWithCurrentStep(currentSteps[currentStepIndex - 1].id);
    }
  };

  const goToStepById = (stepId: Step['id']) => {
    const newIndex = currentSteps.findIndex((s) => s.id === stepId);

    if (newIndex === -1 || newIndex === currentStepIndex) {
      return;
    }
    goToStep(newIndex);
    updateHashWithCurrentStep(stepId);
  };

  return (
    <MultiStepCreationContext
      value={{
        steps: currentSteps,
        onNextStep,
        onPreviousStep,
        goToStepById,
        currentStep,
        currentStepIndex,
        isFirstStep,
        isLastStep,
        getHeaderSteps,
      }}
    >
      {children}
    </MultiStepCreationContext>
  );
};

export const useMultiStepCreationContext = () => useContext(MultiStepCreationContext);
